import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

interface EditSchemaDescriptionDialogProps {
  open: boolean
  /** The current description to seed the field with. */
  initial: string
  onOpenChange: (open: boolean) => void
  /** Persist the (trimmed) description; commits a new schema version. */
  onSave: (description: string) => Promise<void>
}

/** Edit the case schema's description. A schema-level change, so saving commits a
 * new version (SP-385). */
export function EditSchemaDescriptionDialog({
  open,
  initial,
  onOpenChange,
  onSave,
}: EditSchemaDescriptionDialogProps) {
  const [value, setValue] = useState(initial)
  const [busy, setBusy] = useState(false)

  // Reseed the field each time the dialog opens.
  useEffect(() => {
    if (open) setValue(initial)
  }, [open, initial])

  const submit = async () => {
    if (busy) return
    setBusy(true)
    try {
      await onSave(value.trim())
      onOpenChange(false)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground">Schema description</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            A short summary of what this case's schema captures. Saving commits a new version.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="schema-description" className="text-foreground">
            Description
          </Label>
          <Textarea
            id="schema-description"
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder="e.g. People, accounts and communications for Operation Nightfall."
            rows={4}
            className="resize-none bg-muted border-border"
            // biome-ignore lint/a11y/noAutofocus: description is the sole field of an intentional prompt
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-border">
            Cancel
          </Button>
          <Button onClick={submit} disabled={busy}>
            {busy && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
